import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Input } from 'components/Form/Input';
import { Select } from 'components/Form/Select';
import Spacer from 'components/Spacer';
import { ResponsiveCol, ResponsiveRow } from '../../AddEvents/styles';

const InputWrapper = styled.div`
  margin-top: 10px;
`;

const VisibilityOptions = [
  { value: 'visible', label: 'App & POS' },
  { value: 'app_only', label: 'App Only' },
  { value: 'pos_only', label: 'POS Only' },
  { value: 'hidden', label: 'Hidden' },
];

export const getTicketFees = (ticket, ticketType, isTicketHoss) => {
  let fee_dollar, fee_percent;

  if (isTicketHoss) {
    // "Other" tickets have different fee structure (2% only)
    if (ticketType === 'admin_other_tickets') {
      fee_dollar = 0;
      fee_percent = 2;
    } else if (ticket.price >= 1 && ticket.price <= 5) {
      if (ticket.reserved_seat) {
        fee_dollar = 1.25;
      } else {
        fee_dollar = 0.75;
      }
      fee_percent = 3.25;
    } else if (ticket.price > 5) {
      if (ticket.reserved_seat) {
        fee_dollar = 1.75;
      } else {
        fee_dollar = 1.25;
      }
      fee_percent = 3.25;
    }
  } else {
    if (ticketType === 'admin_other_tickets') {
      if (ticket.price >= 1 && ticket.price <= 500) {
        fee_dollar = 0;
        fee_percent = 5.25;
      } else if (ticket.price > 500) {
        fee_dollar = 10;
        fee_percent = 3.25;
      }
    } else {
      if (ticket.price >= 1 && ticket.price <= 1000000) {
        fee_dollar = 1.85;
        fee_percent = 3.25;
      }
    }
  }

  return {
    fee_dollar,
    fee_percent,
  };
};

export const getPosFees = (ticket, isTicketHoss) => {
  let pos_fee_dollar, pos_fee_percent;
  if (isTicketHoss) {
    if (ticket.reserved_seat) {
      pos_fee_dollar = 1.25;
      pos_fee_percent = 3.25;
    } else {
      pos_fee_dollar = 0;
      pos_fee_percent = 5.25;
    }
  } else {
    if (ticket.price >= 1 && ticket.price <= 1000000) {
      pos_fee_dollar = 0;
      pos_fee_percent = 5.25;
    }
  }

  return {
    pos_fee_dollar,
    pos_fee_percent,
  };
};

export const TicketDetail = ({
  ticketType,
  index,
  ticket,
  handleChange,
  errors,
  allCategories,
  isMobile,
  COLOR_CODES,
}) => {
  const isTicketHoss = 'tickethoss' === process.env.REACT_APP_PLATFORM;

  const feesNull = ticket.fee_dollar == null && ticket.fee_percent == null;
  const posFeesNull =
    ticket.pos_fee_dollar == null && ticket.pos_fee_percent == null;

  // Check if set_default_fee is true and set the default fees based on the ticket price
  // Or set it if all the fees are null or undefined
  useEffect(() => {
    if (ticket.set_default_fee || feesNull || posFeesNull || !ticket?.id) {
      let fee_dollar, fee_percent, pos_fee_dollar, pos_fee_percent;

      if (feesNull || (ticket.set_default_fee && !ticket.id)) {
        ({ fee_dollar, fee_percent } = getTicketFees(
          ticket,
          ticketType,
          isTicketHoss
        ));
      } else {
        fee_dollar = ticket.fee_dollar;
        fee_percent = ticket.fee_percent;
      }

      if (posFeesNull || (ticket.set_default_fee && !ticket.id)) {
        ({ pos_fee_dollar, pos_fee_percent } = getPosFees(
          ticket,
          ticketType,
          isTicketHoss
        ));
      } else {
        pos_fee_dollar = ticket.pos_fee_dollar;
        pos_fee_percent = ticket.pos_fee_percent;
      }

      handleChange({
        fields: {
          fee_dollar: fee_dollar,
          fee_percent: fee_percent,
          pos_fee_dollar: pos_fee_dollar,
          pos_fee_percent: pos_fee_percent,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.price]);

  useEffect(() => {
    // If the ticket has an ID, it means it's already saved, so we don't need to set the fees again
    if (ticket.id || !ticket?.set_default_fee) return;

    const { fee_dollar, fee_percent } = getTicketFees(
      ticket,
      ticketType,
      isTicketHoss
    );

    const { pos_fee_dollar, pos_fee_percent } = getPosFees(
      ticket,
      ticketType,
      isTicketHoss
    );

    handleChange({
      fields: {
        fee_dollar: fee_dollar,
        fee_percent: fee_percent,
        pos_fee_dollar: pos_fee_dollar,
        pos_fee_percent: pos_fee_percent,
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.reserved_seat]);

  return (
    <>
      <ResponsiveRow>
        <ResponsiveCol style={{ flex: !isMobile ? '0 0 70%' : '0 0 100%' }}>
          <Input
            id={`${ticketType}.${index}.name`}
            name={`${ticketType}.${index}.name`}
            error={
              errors[`${ticketType}.${index}.name`]
                ? errors[`${ticketType}.${index}.name`]
                : ''
            }
            label="Ticket Name"
            placeholder=""
            onChange={({ target }) => {
              handleChange({
                target: {
                  name: `name`,
                  value: target.value,
                },
              });
              errors[`${ticketType}.${index}.name`] = '';
            }}
            value={ticket.name ?? ''}
          />
        </ResponsiveCol>
        <ResponsiveCol>
          <Input
            id={`${ticketType}.${index}.price`}
            name={`${ticketType}.${index}.price`}
            error={
              errors[`${ticketType}.${index}.price`]
                ? errors[`${ticketType}.${index}.price`]
                : ''
            }
            label="Ticket Price"
            type="number"
            placeholder=""
            onChange={({ target }) => {
              handleChange({
                target: {
                  name: `price`,
                  value: target.value ? parseFloat(target.value) : '',
                },
              });
              errors[`${ticketType}.${index}.price`] = '';
            }}
            value={ticket.price ?? ''}
          />
        </ResponsiveCol>
      </ResponsiveRow>

      <ResponsiveRow>
        <ResponsiveCol style={{ flex: !isMobile ? '0 0 70%' : '0 0 100%' }}>
          <Input
            as="textarea"
            rows={6}
            inputStyle={{ minHeight: 'unset' }}
            id={`${ticketType}.${index}.description`}
            name={`${ticketType}.${index}.description`}
            label="Description"
            placeholder=""
            onChange={({ target }) => {
              handleChange({
                target: {
                  name: `description`,
                  value: target.value,
                },
              });
            }}
            value={ticket.description ?? ''}
          />
        </ResponsiveCol>

        <ResponsiveCol>
          <InputWrapper>
            <Input
              style={{ marginRight: '15px' }}
              id={`${ticketType}.${index}.track_price`}
              name={`${ticketType}.${index}.track_price`}
              label="At Track Price"
              type="number"
              placeholder=""
              onChange={({ target }) =>
                handleChange({
                  target: {
                    name: `track_price`,
                    value: parseFloat(target.value),
                  },
                })
              }
              value={ticket.track_price ?? ''}
            />
          </InputWrapper>

          <Spacer size={6} />
          <Select
            id={`${ticketType}.${index}.color_code`}
            name={`${ticketType}.${index}.color_code`}
            error={
              errors[`${ticketType}.${index}.color_code`]
                ? errors[`${ticketType}.${index}.color_code`]
                : ''
            }
            label="Color Code"
            placeholder="Select a Color"
            options={COLOR_CODES}
            onChange={({ target }) => {
              handleChange({
                target: {
                  name: `color_code`,
                  value: target.value,
                },
              });
              errors[`${ticketType}.${index}.color_code`] = '';
            }}
            value={ticket.color_code}
            backgroundColor={ticket.color_code}
            changeTextColor={true}
          />
        </ResponsiveCol>
      </ResponsiveRow>

      <ResponsiveRow>
        <ResponsiveCol style={{ flex: !isMobile ? '0 0 70%' : '0 0 100%' }}>
          <ResponsiveRow>
            <ResponsiveCol style={{ padding: isMobile ? '5px 0' : '5px 15px' }}>
              <Input
                style={{ marginRight: '15px' }}
                id={`${ticketType}.${index}.fee_dollar`}
                name={`${ticketType}.${index}.fee_dollar`}
                label="Fee Dollar Amount (App)"
                type="number"
                placeholder=""
                onChange={({ target }) => {
                  if (ticket.set_default_fee)
                    handleChange({
                      fields: {
                        fee_dollar: parseFloat(target.value),
                        set_default_fee: false,
                      },
                    });
                  else
                    handleChange({
                      target: {
                        name: `fee_dollar`,
                        value: parseFloat(target.value),
                      },
                    });
                }}
                value={ticket.fee_dollar ?? ''}
              />
            </ResponsiveCol>

            <ResponsiveCol style={{ padding: isMobile ? '5px 0' : '5px 15px' }}>
              <Input
                style={{ marginRight: '15px' }}
                id={`${ticketType}.${index}.fee_percent`}
                name={`${ticketType}.${index}.fee_percent`}
                label="Fee Percent (App)"
                type="number"
                placeholder=""
                onChange={({ target }) => {
                  if (ticket.set_default_fee)
                    handleChange({
                      fields: {
                        fee_percent: parseFloat(target.value),
                        set_default_fee: false,
                      },
                    });
                  else
                    handleChange({
                      target: {
                        name: `fee_percent`,
                        value: parseFloat(target.value),
                      },
                    });
                }}
                value={ticket.fee_percent ?? ''}
              />
            </ResponsiveCol>
          </ResponsiveRow>
        </ResponsiveCol>

        <ResponsiveCol>
          <Select
            id={`${ticketType}.${index}.category_id`}
            name={`${ticketType}.${index}.category_id`}
            error={
              errors[`${ticketType}.${index}.category_id`]
                ? errors[`${ticketType}.${index}.category_id`]
                : ''
            }
            label="Ticket Category"
            placeholder="Category"
            options={allCategories}
            onChange={({ target }) => {
              handleChange({
                target: {
                  name: `category_id`,
                  value: +target.value,
                },
              });
              errors[`${ticketType}.${index}.category_id`] = '';
            }}
            value={ticket.category_id}
          />
        </ResponsiveCol>
      </ResponsiveRow>

      <ResponsiveRow>
        <ResponsiveCol style={{ flex: !isMobile ? '0 0 70%' : '0 0 100%' }}>
          <ResponsiveRow>
            <ResponsiveCol style={{ padding: isMobile ? '5px 0' : '5px 15px' }}>
              <Input
                style={{ marginRight: '15px' }}
                id={`${ticketType}.${index}.pos_fee_dollar`}
                name={`${ticketType}.${index}.pos_fee_dollar`}
                label="Fee Dollar Amount (POS)"
                type="number"
                placeholder=""
                onChange={({ target }) => {
                  if (ticket.set_default_fee)
                    handleChange({
                      fields: {
                        pos_fee_dollar: parseFloat(target.value),
                        set_default_fee: false,
                      },
                    });
                  else
                    handleChange({
                      target: {
                        name: `pos_fee_dollar`,
                        value: parseFloat(target.value),
                      },
                    });
                }}
                value={ticket.pos_fee_dollar ?? ''}
              />
            </ResponsiveCol>

            <ResponsiveCol style={{ padding: isMobile ? '5px 0' : '5px 15px' }}>
              <Input
                style={{ marginRight: '15px' }}
                id={`${ticketType}.${index}.pos_fee_percent`}
                name={`${ticketType}.${index}.pos_fee_percent`}
                label="Fee Percent (POS)"
                type="number"
                placeholder=""
                onChange={({ target }) => {
                  if (ticket.set_default_fee)
                    handleChange({
                      fields: {
                        pos_fee_percent: parseFloat(target.value),
                        set_default_fee: false,
                      },
                    });
                  else
                    handleChange({
                      target: {
                        name: `pos_fee_percent`,
                        value: parseFloat(target.value),
                      },
                    });
                }}
                value={ticket.pos_fee_percent ?? ''}
              />
            </ResponsiveCol>
          </ResponsiveRow>
        </ResponsiveCol>

        <ResponsiveCol>
          <Select
            id={`${ticketType}.${index}.visibility`}
            name={`${ticketType}.${index}.visibility`}
            error={
              errors[`${ticketType}.${index}.visibility`]
                ? errors[`${ticketType}.${index}.visibility`]
                : ''
            }
            label="Visibility"
            placeholder="Visibility"
            options={VisibilityOptions}
            onChange={({ target }) => {
              handleChange({
                target: {
                  name: `visibility`,
                  value: target.value,
                },
              });
              errors[`${ticketType}.${index}.visibility`] = '';
            }}
            value={ticket.visibility ?? 'visible'}
          />
        </ResponsiveCol>
      </ResponsiveRow>
    </>
  );
};
